<template>
    <component
        :is="tag"
        class="tooltip"
        ref="wrapperRef"
        @mouseenter="openTooltip"
        @mouseleave="closeTooltip"
        @click="openTooltip"
    >
        <slot />
        <teleport :disabled="!teleportTo" :to="teleportTo">
            <span
                ref="tooltipRef"
                :class="['tooltip__floating', `tooltip__floating--${placement}`]"
                :style="floatingStyles"
                v-if="tooltipVisibility && tooltipActive"
            >
                <slot name="tooltip">
                    {{ text }}
                </slot>
                <button v-if="!isDesktop" class="tooltip__close" @click.stop="closeTooltip">
                    <SvgIcon name="close" />
                </button>
                <span ref="arrowRef" class="tooltip__arrow" :style="arrowStyles"></span>
            </span>
        </teleport>
    </component>
</template>

<script lang="ts" setup>
import { computed, ref, useSlots } from "vue";
import { arrow, autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/vue";
import useIsBreakpoint from "@/shared/composables/use-is-breakpoint";
import SvgIcon from "@/shared/components/atoms/SvgIcon.vue";

type Position = "top" | "bottom" | "left" | "right";

const props = withDefaults(
    defineProps<{
        text?: string;
        tag?: string;
        hideTooltip?: boolean;
        position?: Position;
        showOnlyOnOverflow?: boolean;
        mobile?: boolean;
        teleportTo?: string;
    }>(),
    {
        text: "",
        tag: "span",
        hideTooltip: false,
        position: "top",
        showOnlyOnOverflow: false,
        mobile: false,
        teleportTo: null,
    },
);
const slots = useSlots();

const { isDesktop } = useIsBreakpoint();

const wrapperRef = ref<HTMLDivElement>();
const tooltipRef = ref<HTMLDivElement>();
const arrowRef = ref<HTMLDivElement>();

const tooltipActive = ref(false);

const isOverflowing = ref(false);

const arrowSize = 8;

const { floatingStyles, middlewareData, placement } = useFloating(wrapperRef, tooltipRef, {
    whileElementsMounted: autoUpdate,
    strategy: "absolute",
    placement: props.position,
    middleware: [offset(arrowSize), flip(), shift(), arrow({ element: arrowRef })],
});

const arrowStyles = computed(() => {
    const oppositePosition = {
        top: "bottom",
        bottom: "top",
        left: "right",
        right: "left",
    }[placement.value?.split("-")[0]];

    return {
        left: middlewareData.value.arrow?.x != null ? `${middlewareData.value.arrow.x}px` : "",
        top: middlewareData.value.arrow?.y != null ? `${middlewareData.value.arrow.y}px` : "",
        [oppositePosition]: `-${arrowSize - 2}px`,
    };
});

const calculateOverflow = () => {
    if (!wrapperRef.value) return;

    isOverflowing.value = wrapperRef.value.scrollWidth > wrapperRef.value.clientWidth;
};

const tooltipVisibility = computed(() => {
    const hasText = props.text.length > 0 || slots.tooltip;
    const overflowCondition = props.showOnlyOnOverflow ? isOverflowing.value : true;
    const resolutionCondition = props.mobile ? true : isDesktop.value;

    return !props.hideTooltip && hasText && overflowCondition && resolutionCondition;
});

let mouseEnterTimeout: number | null = null;

const openTooltip = (event: MouseEvent | TouchEvent) => {
    if ((event.type === "click" && isDesktop.value) || (event.type === "mouseenter" && !isDesktop.value)) return;

    if (event.type === "click" && !isDesktop.value && props.mobile) {
        event.stopPropagation();
    }

    calculateOverflow();

    if (!tooltipVisibility.value) return;

    mouseEnterTimeout = window.setTimeout(() => {
        tooltipActive.value = true;
    }, 300);
};

const closeTooltip = () => {
    if (mouseEnterTimeout) {
        window.clearTimeout(mouseEnterTimeout);
        mouseEnterTimeout = null;
    }
    tooltipActive.value = false;
};
</script>
